/**
 * DayDetailDrawer — slide-from-right panel showing the rich detail of a
 * specific day without hiding the calendar. Designed to feel premium and
 * meditative: blurred glass surface, generous spacing, rounded chips.
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, MoonStar, Activity, Heart, Brain, BookOpen, Sparkles, Quote, Pencil, Lock } from "lucide-react";
import { calendarColor, calendarBandLabel, formatDateLong } from "@/lib/calendar";
import { formatDateAR } from "@/lib/dates";
import { DIMENSION_LABELS, formatScore } from "@/lib/scoring";

const DIM_ICON = {
  sleep: MoonStar,
  physical: Activity,
  mood: Heart,
  thoughts: Brain,
  learning: BookOpen,
};

export default function DayDetailDrawer({ open, loading, day, onClose, onEdit }) {
  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Rendered through a portal so that the drawer is always positioned
  // relative to the viewport. Avoids broken `position: fixed` whenever an
  // ancestor uses CSS transforms (e.g. the parent `animate-fade-up`).
  if (typeof document === "undefined") return null;

  return createPortal(
    <aside
      className={`fixed top-0 right-0 h-full w-full sm:w-[420px] z-40 transition-transform duration-500 ease-out ${
        open ? "translate-x-0" : "translate-x-full pointer-events-none"
      }`}
      aria-hidden={!open}
      data-testid="day-detail-drawer"
      data-open={open ? "true" : "false"}
    >
      <div
        className="h-full overflow-y-auto"
        style={{
          background:
            "linear-gradient(180deg, rgba(255, 251, 240, 0.96) 0%, rgba(245, 236, 213, 0.93) 100%)",
          backdropFilter: "blur(24px) saturate(120%)",
          WebkitBackdropFilter: "blur(24px) saturate(120%)",
          borderLeft: "1px solid rgba(218, 207, 186, 0.55)",
          boxShadow: "-30px 0 60px -30px rgba(83, 109, 83, 0.25)",
        }}
      >
        {!open ? null : loading || !day ? (
          <div className="p-6">
            <SkeletonRow />
            <div className="mt-6 space-y-4">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          </div>
        ) : (
          <DrawerBody day={day} onClose={onClose} onEdit={onEdit} />
        )}
      </div>
    </aside>,
    document.body
  );
}

function DrawerBody({ day, onClose, onEdit }) {
  const color = calendarColor(day.score);
  const band = calendarBandLabel(day.score);
  const avg = day.average_overall;
  const delta = day.score != null && avg != null ? day.score - avg : null;

  return (
    <div className="relative">
      {/* Atmospheric accent header */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-64 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${
            day.score != null ? color : "rgba(196,178,144,0.32)"
          } 0%, transparent 65%)`,
          opacity: 0.55,
        }}
      />

      <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
        <div>
          <p className="tk-label">Detalle del día</p>
          <h2
            className="font-outfit text-2xl text-ink-900 mt-1"
            data-testid="drawer-date"
          >
            {formatDateAR(day.date)}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="h-9 w-9 rounded-full flex items-center justify-center text-ink-600 hover:text-ink-900 transition border backdrop-blur-md"
          style={{
            background: "rgba(255, 251, 240, 0.75)",
            borderColor: "rgba(218, 207, 186, 0.55)",
          }}
          data-testid="drawer-close"
        >
          <X size={16} />
        </button>
      </div>

      {!day.has_log ? (
        <>
          <NoLogContent />
          <EditAction day={day} onEdit={onEdit} createMode />
        </>
      ) : (
        <>
          <div className="px-6">
            <div
              className="rounded-3xl p-5 flex items-end gap-4 border"
              style={{
                background:
                  "linear-gradient(155deg, rgba(255, 251, 240, 0.85) 0%, rgba(244, 236, 215, 0.55) 100%)",
                borderColor: "rgba(218, 207, 186, 0.6)",
              }}
            >
              <div>
                <p className="tk-label">Score del día</p>
                <p className="flex items-baseline gap-2 mt-1">
                  <span className="font-outfit text-5xl text-ink-900" data-testid="drawer-score">
                    {formatScore(day.score)}
                  </span>
                  <span className="font-dmsans text-sm text-ink-600">/ 10</span>
                </p>
                <p className="font-dmsans text-xs text-ink-600 mt-1">{band}</p>
              </div>
              {delta != null && (
                <div
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                  style={{
                    background: delta >= 0 ? "rgba(42,157,143,0.12)" : "rgba(224,122,95,0.14)",
                    color: delta >= 0 ? "#1B6F66" : "#B8624C",
                  }}
                  data-testid="drawer-delta"
                >
                  <Sparkles size={12} />
                  <span className="font-dmsans text-xs">
                    {delta >= 0 ? "+" : ""}
                    {delta.toFixed(1)} vs tu media
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Dimensions */}
          <section className="px-6 mt-6" data-testid="drawer-dimensions">
            <p className="tk-label mb-3">Dimensiones</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(day.dimensions).map(([id, dim]) => {
                const Icon = DIM_ICON[id] || Sparkles;
                const score = dim.score;
                const dimColor = calendarColor(score);
                return (
                  <div
                    key={id}
                    className="rounded-2xl p-3 flex items-center gap-3 border"
                    style={{
                      background: "rgba(255, 251, 240, 0.75)",
                      borderColor: "rgba(218, 207, 186, 0.5)",
                    }}
                    data-testid={`drawer-dim-${id}`}
                  >
                    <span
                      className="flex items-center justify-center rounded-full"
                      style={{
                        width: 34,
                        height: 34,
                        background: dimColor,
                        color: "#2C3532",
                      }}
                    >
                      <Icon size={16} />
                    </span>
                    <div>
                      <p className="font-dmsans text-[10px] tracking-[0.14em] uppercase text-ink-600">
                        {DIMENSION_LABELS[id] || dim.name}
                      </p>
                      <p className="font-outfit text-lg text-ink-900 leading-none mt-1">
                        {formatScore(score)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Emotions */}
          {(day.predominant_emotion || (day.emotions || []).length > 0) && (
            <section className="px-6 mt-6" data-testid="drawer-emotions">
              <p className="tk-label mb-3">Cómo te sentiste</p>
              {day.predominant_emotion && (
                <p className="font-outfit text-lg text-ink-900 mb-2">
                  Predominó <span className="text-sage-700">{day.predominant_emotion.toLowerCase()}</span>.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {(day.emotions || []).map((e, i) => (
                  <span
                    key={i}
                    className="font-dmsans text-xs px-3 py-1.5 rounded-full"
                    style={{
                      background: "rgba(222, 232, 221, 0.55)",
                      color: "#536D53",
                      border: "1px solid rgba(180,195,175,0.4)",
                    }}
                  >
                    {e}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Reflection */}
          {day.reflection && (
            <section className="px-6 mt-6" data-testid="drawer-reflection">
              <p className="tk-label mb-3">Reflexión</p>
              <blockquote
                className="relative rounded-3xl p-5 pl-12 border"
                style={{
                  background:
                    "linear-gradient(140deg, rgba(215, 226, 210, 0.45) 0%, rgba(244, 217, 203, 0.32) 100%)",
                  borderColor: "rgba(180, 195, 175, 0.4)",
                }}
              >
                <Quote
                  size={20}
                  className="absolute"
                  style={{ top: 18, left: 18, color: "rgba(83,109,83,0.45)" }}
                />
                <p className="font-outfit text-[15px] leading-relaxed text-ink-900 whitespace-pre-wrap">
                  {day.reflection}
                </p>
              </blockquote>
            </section>
          )}

          {/* Patterns */}
          {(day.patterns || []).length > 0 && (
            <section className="px-6 mt-6" data-testid="drawer-patterns">
              <p className="tk-label mb-3">Patrones observados</p>
              <ul className="space-y-2">
                {day.patterns.map((p, i) => (
                  <li
                    key={i}
                    className="rounded-2xl px-4 py-3 flex items-start gap-3 border"
                    style={{
                      background:
                        p.tone === "positive"
                          ? "rgba(222, 232, 221, 0.55)"
                          : "rgba(248, 220, 210, 0.45)",
                      borderColor:
                        p.tone === "positive"
                          ? "rgba(141,187,142,0.5)"
                          : "rgba(224,122,95,0.4)",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                      style={{
                        background: p.tone === "positive" ? "#2A9D8F" : "#C97A60",
                      }}
                    />
                    <p className="font-dmsans text-sm text-ink-900 leading-relaxed">{p.text}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <EditAction day={day} onEdit={onEdit} />
        </>
      )}

      <div className="h-6" />
    </div>
  );
}

function EditAction({ day, onEdit, createMode = false }) {
  const editable = day?.editable;
  const editWindow = day?.edit_window_days || 30;
  return (
    <section className="px-6 mt-6 mb-2" data-testid="drawer-edit-action">
      {editable ? (
        <button
          type="button"
          onClick={() => onEdit && onEdit(day.date)}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-dmsans text-sm font-medium text-white transition active:scale-[0.98]"
          style={{
            background: "linear-gradient(135deg, #2A9D8F 0%, #1B6F66 100%)",
            boxShadow: "0 12px 28px -12px rgba(42,157,143,0.55)",
          }}
          data-testid="drawer-edit-btn"
        >
          <Pencil size={14} />
          {createMode ? "Crear registro de este día" : "Editar registro"}
        </button>
      ) : (
        <div
          className="rounded-2xl px-4 py-3 flex items-start gap-3 border"
          style={{
            background: "rgba(255, 251, 240, 0.7)",
            borderColor: "rgba(218, 207, 186, 0.5)",
          }}
          data-testid="drawer-not-editable"
        >
          <Lock size={14} className="mt-0.5 shrink-0 text-ink-600" />
          <p className="font-dmsans text-xs text-ink-600 leading-relaxed">
            Los registros pueden editarse hasta {editWindow} días hacia atrás.
          </p>
        </div>
      )}
    </section>
  );
}

function NoLogContent() {
  return (
    <div className="px-6 py-4">
      <div
        className="rounded-3xl p-6 text-center border"
        style={{
          background:
            "linear-gradient(140deg, rgba(255, 251, 240, 0.85) 0%, rgba(244, 236, 215, 0.55) 100%)",
          borderColor: "rgba(218, 207, 186, 0.55)",
        }}
        data-testid="drawer-empty"
      >
        <div
          className="mx-auto mb-4 rounded-full"
          style={{
            width: 56,
            height: 56,
            background: "rgba(222, 232, 221, 0.55)",
            border: "1px dashed rgba(141,187,142,0.5)",
          }}
        />
        <p className="font-outfit text-lg text-ink-900">Este día no tiene registro</p>
        <p className="font-dmsans text-sm text-ink-600 mt-2">
          Cada día que registrás aporta una pieza más a tu jardín. Volvé al presente y sumá uno hoy.
        </p>
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="h-4 w-2/3 rounded-full bg-sand-100/80 animate-pulse" />
  );
}
